import { useEffect, useRef } from "react";
import { trackFormAbandon, trackFormFieldFocus, trackFormStart } from "@/utils/tracking";

export interface FormFillState {
  name_filled: boolean;
  email_filled: boolean;
  consents_checked: number;
  consents_total: number;
}

/**
 * Field-set-agnostic form-friction instrumentation core (ADR-0087). Owns the
 * started/submitted/abandon lifecycle and fires the four friction events:
 * `form_start` (once, on first field focus), `form_field_focus` (per focus),
 * and `form_abandon` (on pagehide/visibilitychange-hidden when the form was
 * started but not submitted, carrying the fill-state). `form_field_error` is
 * fired by the caller on validation failure. Consumed by every lead form and
 * the paid CheckoutForm so the events cannot re-diverge across form copies.
 *
 * `formVariant` tags every emitted event with which form instance fired it —
 * required on any page rendering more than one form (the guide pages' hero +
 * bottom pair), since the events are otherwise identical. Omit it for
 * single-form pages.
 */
export function useFormInstrumentation(getFillState: () => FormFillState, formVariant?: string) {
  const startedRef = useRef(false);
  const submittedRef = useRef(false);
  const abandonFiredRef = useRef(false);
  const fillRef = useRef(getFillState);
  fillRef.current = getFillState;
  const variantRef = useRef(formVariant);
  variantRef.current = formVariant;

  useEffect(() => {
    const maybeAbandon = () => {
      if (!startedRef.current || submittedRef.current || abandonFiredRef.current) return;
      abandonFiredRef.current = true;
      trackFormAbandon(fillRef.current(), variantRef.current);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") maybeAbandon();
    };
    window.addEventListener("pagehide", maybeAbandon);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", maybeAbandon);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const onFieldFocus = (field: string) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackFormStart(formVariant);
    }
    trackFormFieldFocus(field, formVariant);
  };

  const markSubmitted = () => {
    submittedRef.current = true;
  };

  return { onFieldFocus, markSubmitted };
}
